import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { withRouter } from 'react-router-dom';
import Footer from '../../components/Footer';
import 'wicg-inert';
import ScrollToTop from "../../libs/scrollToTop";
import { useKonami } from 'react-konami-code';
import Nav from '../../components/Nav.js';
import SecondaryNav from '../../components/SecondaryNav';

import ModalOverlay from "../../components/ModalOverlay";

import heroMobileImage from '../../images/articles/P00138022_WF_BRAND_FY25_ISF_AprUpdate21_LP_767x577.jpg';
import heroDesktopImage from '../../images/articles/P00138022_WF_BRAND_FY25_ISF_AprUpdate21_LP_1164x584.jpg';

import { fireTag } from '../../libs/analytics.js';

const ClimateResilience = ({
    handleEvent,
    closeExitOverlay,
    addExitClickListeners,
    modalOverlay,
}) => {

    const [enableScrollGA, setEnableScrollGA] = useState(false);
    const [shouldBeInert, setShouldBeInert] = useState(false);
    const [gridVisible, setGridVisible] = useState(false);

    const heroOGImage = 'P00138022_WF_BRAND_FY25_ISF_AprUpdate21_LP_ogimage';

	addExitClickListeners();

    const toggleInert = () => {
        setShouldBeInert(!shouldBeInert)
    }

    const linkDisclosures = () => {
		const bodyDisclosures = document.querySelectorAll('.body-disclosures');
		const footerDisclosures = document.querySelectorAll('.footer-disclosures');
		bodyDisclosures.forEach(elem => {
			elem.addEventListener('click', (e) => {
				e.preventDefault();
				let disclosureNumber = Number(e.target.innerHTML)
				let targetElement = footerDisclosures[disclosureNumber - 1];
				window.scrollTo({
					left: 0,
					top: targetElement.offsetTop,
					behavior: 'smooth'
				});
				window.setTimeout(() => {
					targetElement.focus();
				}, 1000);
			});
		});

		footerDisclosures.forEach(elem => {
			elem.addEventListener('click', (e) => {
				e.preventDefault();
				let disclosureNumber = Number(e.target.innerHTML)
				let targetElement = bodyDisclosures[disclosureNumber - 1];
				fireTag('A2',disclosureNumber);
				window.scrollTo({
					left: 0,
					top: targetElement.offsetTop,
					behavior: 'smooth'
				});
				window.setTimeout(() => {
					targetElement.focus();
				}, 1000);
			});
		});
	}

    let mastheadImage = window.innerWidth < 768 ? heroMobileImage : heroDesktopImage;

    return (
		<>
			<Helmet>
				<title>Energy innovation powers climate resilience</title>
				<meta
					name="description"
					content="Emerging technologies can support communities before, during, and after disaster strikes."
				/>
				<meta property="og:title" content="Energy innovation powers climate resilience"/>
				<meta
					property="og:description"
					content="Emerging technologies can support communities before, during, and after disaster strikes."
				/>
				<meta property="og:image" content={`https://welcome.wf.com/impact/images/articles/${heroOGImage}`}/>

				<meta property="twitter:title" content="Energy innovation powers climate resilience"/>
				<meta
					property="twitter:description"
					content="Emerging technologies can support communities before, during, and after disaster strikes."
				/>
				i
			</Helmet>
			{/*TODO: isMobile should be passed in from route, not calc'd here */}

			<Nav handleEvent={handleEvent} isNotHomePage={false} activePage="articles" isMobile={window.innerWidth < 768} showExternalNav toggleInert={toggleInert} shouldBeInert={shouldBeInert}/>

			{!window.innerWidth < 768 && (
				<SecondaryNav page="Articles" shouldBeInert={shouldBeInert} activePage="articles"/>
			)}


			<main className="flex-container">

				<div className="flex-row">
					<div className="flex-col-sm-12 flex-col-md-8 text-center headline">
						<h1 id="articles-main-h1" style={{marginBottom: 10}}>
                        Energy innovation powers climate resilience                       
                        </h1>
						<h2 className="summary">
                        Emerging technologies can support communities before, during, and after disaster strikes.
                        </h2>
					</div>

				</div>

				<div className="flex-row full-width-md">
					<div className="flex-col-lg-10 flex-col-md-10 text-center hero-container">
						<img src={mastheadImage} alt="Three executives stand at a table and discuss a scale model with buildings and trees."/>
					</div>
				</div>
                
				<div className="flex-row">
					<div className="flex-col-md-8 flex-col-sm-12 text-center article-intro info-graphic">
                    <div className="by-line">
							<p>By Jill Bunting, <span>senior lead sustainable finance integration specialist at Wells Fargo</span></p>
						</div>
						<div className="article-info">
							<div>
								<p>April 17, 2025</p>
							</div>
							<div>
								<p>4-minute read</p>
							</div>
						</div>
                    <p>Extreme weather events are getting worse. The <a onClick={()=>fireTag('A1','national aeronautics and space administration')} target="_blank" href="https://science.nasa.gov/climate-change/extreme-weather/" rel="noreferrer" data-click-listener="true">National Aeronautics and Space Administration</a> (NASA) says heat waves, severe floods, droughts, wildfires, and hurricanes are becoming more frequent and more intense. But advanced energy technology is offering new ways to make communities across the country more resilient and adaptable.</p>
                    <p>The <a onClick={()=>fireTag('A1','wells fargo innovation incubator')} target="_blank" href="https://in2ecosystem.com/" rel="noreferrer" data-click-listener="true">Wells Fargo Innovation Incubator</a> (IN2) is a $55 million energy technology program funded by the Wells Fargo Foundation and co-administered by the U.S. Department of Energy’s National Renewable Energy Laboratory. Since 2014, the program has supported technology startups with partnerships, early-stage investments, and hands-on testing to advance energy solutions — from concept to commercialization.</p>
                    <h2 className="subhead" style={{marginTop: '1rem'}}>
                        New technologies can address grid challenges to keep the lights on
                    </h2>
                    <p>In California, <a onClick={()=>fireTag('A1','about 10%')} target="_blank" href="https://lao.ca.gov/Publications/Report/4952" rel="noreferrer" data-click-listener="true">about 10%</a> of wildfires are started by utility equipment. In fact, some of the <a onClick={()=>fireTag('A1','most destructive wildfires')} target="_blank" href="https://www.nytimes.com/interactive/2025/01/13/us/los-angeles-fires-cause.html" rel="noreferrer" data-click-listener="true">most destructive wildfires</a> are linked to problems with utility poles and power lines. These incidents demonstrate vulnerabilities in energy infrastructure with power lines that <a onClick={()=>fireTag('A1','overheat or sag')} target="_blank" href="https://www.wbfo.org/2024-03-12/old-power-lines-plus-climate-change-mean-a-growing-risk-of-utilities-starting-fires" rel="noreferrer" data-click-listener="true">overheat or sag</a> onto trees or dry brush.</p>
                    <p>The impacts are often devastating, with homes burned to the ground, businesses reduced to ash, and lives tragically lost. IN2 portfolio company <a onClick={()=>fireTag('A1','whisker labs')} target="_blank" href="https://www.whiskerlabs.com/" rel="noreferrer" data-click-listener="true">Whisker Labs</a> uses advanced sensor technology to detect early signs of electrical hazards like faults and sparks on power lines. The real-time grid performance data and predictive intelligence can be used by utility companies for timely intervention and mitigation.</p>
                    <p>During the January 2025 wildfires in Los Angeles, Whisker Labs’ <a onClick={()=>fireTag('A1','data showed')} target="_blank" href="https://nypost.com/2025/01/11/us-news/power-surges-began-before-california-wildfires-ignited-report/" rel="noreferrer" data-click-listener="true">data showed</a> a sharp increase in faults just before the Eaton, Palisades, and Hurst fires. Faults can trigger electrical arcs <a onClick={()=>fireTag('A1','creating ignition points')} target="_blank" href="https://www.nrel.gov/docs/fy23osti/80746.pdf" rel="noreferrer" data-click-listener="true">creating ignition points</a> that may cause vegetation or other material to ignite.</p>
                    <h2 className="subhead" style={{marginTop: '1rem'}}>
                        Renewable energy and battery systems are key to power through disruptions
                    </h2>
                    <p>From 2000 to 2023, <a onClick={()=>fireTag('A1','80% of major power outages')} target="_blank" href="https://www.climatecentral.org/climate-matters/weather-related-power-outages-rising" rel="noreferrer" data-click-listener="true">80% of major power outages</a> in the U.S. were attributed to weather events. <a onClick={()=>fireTag('A1','distributed renewable energy generation')} target="_blank" href="https://www.nrel.gov/docs/fy18osti/71310.pdf" rel="noreferrer" data-click-listener="true">Distributed renewable energy generation</a> accompanied by battery storage is designed to operate in severe conditions to provide back-up power to impacted communities.</p>
                    <p>IN2 portfolio company <a onClick={()=>fireTag('A1','yotta energy')} target="_blank" href="https://www.yottaenergy.com/" rel="noreferrer" data-click-listener="true">Yotta Energy</a> integrates battery storage with solar energy for commercial buildings. The company’s units are placed beneath rooftop solar panels and use passive thermal regulation to keep batteries at the ideal working temperature.</p>
                    <p>In September 2021, Yotta Energy partnered with <a onClick={()=>fireTag('A1','ecobuild')} target="_blank" href="https://www.ecobuildus.com/" rel="noreferrer" data-click-listener="true">EcoBuild</a> to <a onClick={()=>fireTag('A1','offer solar and energy storage')} target="_blank" href="https://www.yottaenergy.com/ar-louisiana" rel="noreferrer" data-click-listener="true">offer solar and energy storage</a> throughout Louisiana — a state impacted by hurricanes, tornados, and other extreme weather events. These energy solutions were provided to grocery stores, hotels, and other businesses, allowing them to remain open and serve their communities during and after an emergency.</p>
                    <p><a onClick={()=>fireTag('A1','ess inc.')} target="_blank" href="https://essinc.com/industrial-microgrids/" rel="noreferrer" data-click-listener="true">ESS Inc.</a> and <a onClick={()=>fireTag('A1','span')} target="_blank" href="https://www.span.io/" rel="noreferrer" data-click-listener="true">SPAN</a> are two other IN2 portfolio companies offering technology that can boost resilience during power outages. ESS’s iron flow batteries are designed for long-duration energy storage, delivering renewable energy when it’s needed. The company’s batteries are made of iron, salt, and water, providing up to 12 hours of storage capacity and delivering a longer lifespan than traditional batteries.</p>
                    <p>SPAN’s technology can also help homeowners extend on-site battery life by allowing users to prioritize power to essential appliances like refrigerators and medical devices when battery charge is low. The system then <a onClick={()=>fireTag('A1','shifts battery power')} target="_blank" href="https://www.google.com/url?sa=t&rct=j&q=&esrc=s&source=web&cd=&cad=rja&uact=8&ved=2ahUKEwjWtNDd_7uLAxXKFFkFHe0zHS0QFnoECDkQAQ&url=https%3A%2F%2Fefiling.energy.ca.gov%2FGetDocument.aspx%3Ftn%3D247129%26DocumentContentId%3D81538&usg=AOvVaw0kcweop67pcKupYLfPJmvm&opi=89978449" rel="noreferrer" data-click-listener="true">shifts battery power</a> back to other appliances when the battery recharges.</p>
                    <p>These innovative technologies can help people and communities nationwide become more self-reliant. IN2 provides the expert and financial support to scale these energy solutions and inspire widespread adoption to foster resilience.</p>
					</div>
				</div>
			</main>


			<Footer id="footer" footerStyles="global" shouldBeInert={shouldBeInert} activePage="sustainability" enableScrollGA={enableScrollGA}/>

			{modalOverlay && modalOverlay.open && (
				<ModalOverlay closeExitOverlay={closeExitOverlay} modalOverlay={modalOverlay}/>
			)}
		</>
    );

};

export default withRouter(ClimateResilience);



